import { Match, Show, Switch, createSignal, onCleanup, onMount } from "solid-js";
import type { AuthModule, AuthSession } from "~/features/auth/auth";
import { ClassFormModal } from "~/features/classes/ClassFormModal";
import { ClassesPage } from "~/features/classes/ClassesPage";
import { createClassesModule, type TeacherClass } from "~/features/classes/classes";
import { createClassTeamModule, isReadOnly } from "~/features/classes/classTeam";
import { ClassTeamModal } from "~/features/classes/ClassTeamModal";
import type { HttpPort } from "~/lib/http";
import { SettingsPage } from "~/features/settings/SettingsPage";
import { createSettingsModule } from "~/features/settings/settings";
import { WeeklyGoalsPage } from "~/features/goals/WeeklyGoalsPage";
import { createGoalsModule } from "~/features/goals/goals";
import { WeekProgressPage } from "~/features/progress/WeekProgressPage";
import { createProgressModule } from "~/features/progress/progress";
import { MoodHistoryPage } from "~/features/mood/MoodHistoryPage";
import { createMoodModule } from "~/features/mood/mood";
import { FeedbackPage } from "~/features/feedback/FeedbackPage";
import { createFeedbackModule } from "~/features/feedback/feedback";
import { ReportPage } from "~/features/report/ReportPage";
import { createReportModule } from "~/features/report/report";
import { HelpRequestsPage } from "~/features/help/HelpRequestsPage";
import { createHelpModule } from "~/features/help/help";
import { createBrowserDownloadPort } from "~/lib/download";
import { createTodayModule } from "~/features/today/today";
import { StudentsPage } from "~/features/students/StudentsPage";
import { createStudentsModule } from "~/features/students/students";
import { VotingPage } from "~/features/voting/VotingPage";
import { createVotingModule } from "~/features/voting/voting";
import { SchoolOverview } from "~/features/school/SchoolOverview";
import { WelcomeCard } from "~/features/school/WelcomeCard";
import { createTeamApi, type DashboardEntry } from "~/features/school/team";
import { TeacherShell, type TeacherView } from "./TeacherShell";
import { TeacherTodayPage } from "./TeacherTodayPage";

export function TeacherDashboard(props: { session: AuthSession; auth: AuthModule; http: HttpPort; entry?: DashboardEntry; onLogout: () => void }) {
  const classesModule = createClassesModule(props.http, props.session.token);
  const classTeamModule = createClassTeamModule(props.http, props.session.token);
  const settingsModule = createSettingsModule(props.http, props.session.token);
  const todayModule = createTodayModule(props.http, props.session.token);
  const studentsModule = createStudentsModule(props.http, props.session.token);
  const votingModule = createVotingModule(props.http, props.session.token);
  const goalsModule = createGoalsModule(props.http, props.session.token);
  const progressModule = createProgressModule(props.http, props.session.token);
  const moodModule = createMoodModule(props.http, props.session.token);
  const feedbackModule = createFeedbackModule(props.http, props.session.token);
  const reportModule = createReportModule(props.http, createBrowserDownloadPort(), props.session.token);
  const helpModule = createHelpModule(props.http, props.session.token);
  const [user, setUser] = createSignal(props.session.user);
  const [classes, setClasses] = createSignal<TeacherClass[]>([]);
  const [selectedClass, setSelectedClass] = createSignal<TeacherClass>();
  const isOwner = props.session.user.schoolRole === "owner";
  const startsAtSchool = isOwner && (props.entry === "principal" || props.entry === "welcome-principal");
  const [view, setView] = createSignal<TeacherView>(startsAtSchool ? "school" : props.entry === "welcome-teacher" ? "classes" : "today");
  const [welcome, setWelcome] = createSignal(props.entry === "welcome-principal" || props.entry === "welcome-teacher" ? props.entry : undefined);
  const [notice, setNotice] = createSignal(props.entry === "principal" && !isOwner ? "Ihr Konto hat keine Schulleitungs-Rechte. Sie sehen Ihre Klassen." : "");
  const teamApi = createTeamApi(props.http, props.session.token);
  const [sidebarOpen, setSidebarOpen] = createSignal(false);
  const [creating, setCreating] = createSignal(false);
  const [showTeam, setShowTeam] = createSignal(false);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal("");
  const [proposalCount, setProposalCount] = createSignal(0);
  const [concernCount, setConcernCount] = createSignal(0);

  const refreshProposalCount = async (classId?: string) => {
    if (!classId) { setProposalCount(0); return; }
    const count = await goalsModule.countPending(classId).catch(() => 0);
    if (selectedClass()?.id === classId) setProposalCount(count);
  };
  const refreshConcernCount = async (classId?: string) => {
    if (!classId) { setConcernCount(0); return; }
    const count = await feedbackModule.countUnread(classId).catch(() => 0);
    if (selectedClass()?.id === classId) setConcernCount(count);
  };

  const loadClasses = async () => {
    const values = await classesModule.list();
    setClasses(values);
    const rememberedId = selectedClass()?.id ?? props.session.currentClass?.id;
    const next = values.find((value) => value.id === rememberedId) ?? values[0];
    setSelectedClass(next);
    props.auth.selectClass(next ? { id: next.id, name: next.name } : undefined);
    void refreshProposalCount(next?.id);
    void refreshConcernCount(next?.id);
    return values;
  };
  const selectClass = (value: TeacherClass) => {
    rememberClass(value);
    void refreshProposalCount(value.id);
    void refreshConcernCount(value.id);
    setView("today");
  };
  const rememberClass = (value: TeacherClass | undefined) => {
    setSelectedClass(value);
    props.auth.selectClass(value ? { id: value.id, name: value.name } : undefined);
  };
  const logout = async () => {
    await props.auth.logout().catch(() => {});
    props.onLogout();
  };
  const passwordChanged = () => {
    props.auth.clearLocalSession();
    props.onLogout();
  };

  onMount(() => {
    document.body.className = "role-teacher";
    void loadClasses().catch((cause) => setError(cause instanceof Error ? cause.message : "Klassen konnten nicht geladen werden")).finally(() => setLoading(false));
  });
  onCleanup(() => { document.body.className = ""; });

  return (
    <TeacherShell
      user={user()}
      classes={classes()}
      selectedClass={selectedClass()}
      proposalCount={proposalCount()}
      concernCount={concernCount()}
      view={view()}
      sidebarOpen={sidebarOpen()}
      onSidebarOpenChange={setSidebarOpen}
      onClassSelect={selectClass}
      onCreateClass={() => setCreating(true)}
      onNavigate={setView}
      onLogout={logout}
    >
      <Show when={notice()}><div class="so-notice" role="status"><span>{notice()}</span><button type="button" class="sr-linkbtn" onClick={() => setNotice("")}>Schließen</button></div></Show>
      <Show when={welcome()}>{(entry) => <WelcomeCard entry={entry() as Exclude<DashboardEntry, "principal">} onClose={() => setWelcome(undefined)} />}</Show>
      <Show when={isReadOnly(selectedClass()) && view() !== "school" && view() !== "classes" && view() !== "settings"}>
        <div class="so-notice" role="status">
          <span>Nur Ansicht: Diese Klasse wird von {selectedClass()?.teacher_name} bearbeitet. Sie sehen alle Einträge, können aber nichts ändern.</span>
          <button type="button" class="sr-linkbtn" onClick={() => setShowTeam(true)}>Team</button>
        </div>
      </Show>
      <Show when={showTeam() && selectedClass()}>{(value) => <ClassTeamModal value={value()} module={classTeamModule} onClose={() => setShowTeam(false)} onChanged={loadClasses} />}</Show>
      <Show when={!loading()} fallback={<div style={{ display: "flex", "justify-content": "center", padding: "40px" }}><div class="loading-spinner" /></div>}>
        <Show when={!error()} fallback={<div class="card"><p>{error()}</p></div>}>
          <Switch fallback={<div style={{ display: "flex", "justify-content": "center", padding: "40px" }}><div class="loading-spinner" /></div>}>
            <Match when={view() === "today"}><TeacherTodayPage value={selectedClass()} module={todayModule} onNavigateVoting={() => setView("voting")} /></Match>
            <Match when={view() === "students"}><StudentsPage value={selectedClass()} module={studentsModule} /></Match>
            <Match when={view() === "voting"}><VotingPage value={selectedClass()} module={votingModule} onNavigateGoals={() => setView("goals")} /></Match>
            <Match when={view() === "goals"}><WeeklyGoalsPage value={selectedClass()} module={goalsModule} votingModule={votingModule} onProposalCountChange={setProposalCount} /></Match>
            <Match when={view() === "progress"}><WeekProgressPage value={selectedClass()} module={progressModule} onNavigateStudents={() => setView("students")} /></Match>
            <Match when={view() === "mood"}><MoodHistoryPage value={selectedClass()} module={moodModule} /></Match>
            <Match when={view() === "feedback"}><FeedbackPage value={selectedClass()} module={feedbackModule} onUnreadCountChange={setConcernCount} /></Match>
            <Match when={view() === "report"}><ReportPage value={selectedClass()} module={reportModule} onNavigateClasses={() => setView("classes")} /></Match>
            <Match when={view() === "help"}><HelpRequestsPage value={selectedClass()} module={helpModule} /></Match>
            <Match when={view() === "school"}><SchoolOverview api={teamApi} onOpenClasses={() => setView("classes")} onClassAssigned={loadClasses} /></Match>
            <Match when={view() === "classes"}>
              <ClassesPage classes={classes()} module={classesModule} teamModule={classTeamModule} onReload={loadClasses} onSelect={selectClass} onRemember={rememberClass} />
            </Match>
            <Match when={view() === "settings"}>
              <SettingsPage
                module={settingsModule}
                classesModule={classesModule}
                teamModule={classTeamModule}
                classes={classes()}
                onClassesReload={loadClasses}
                onClassSelect={selectClass}
                onClassRemember={rememberClass}
                onProfileSaved={(profile) => {
                  const updated = props.auth.updateUser({ firstName: profile.firstName, lastName: profile.lastName, email: profile.email });
                  if (updated) setUser(updated);
                  setClasses((values) => values.map((value) => ({ ...value, teacher_school_name: profile.schoolName || null })));
                  setSelectedClass((value) => value ? { ...value, teacher_school_name: profile.schoolName || null } : value);
                }}
                onPasswordChanged={passwordChanged}
              />
            </Match>
          </Switch>
        </Show>
      </Show>
      <Show when={creating()}>
        <ClassFormModal
          onClose={() => setCreating(false)}
          onSave={async (draft) => {
            const value = await classesModule.create(draft);
            await loadClasses();
            setCreating(false);
            const loaded = classes().find((item) => item.id === value.id) ?? value;
            selectClass(loaded);
          }}
        />
      </Show>
    </TeacherShell>
  );
}
